import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { OutletCacheService } from './outlet-cache.service';
import { OutletRightsDTO } from './model/outlet';
import { OUTLET_ID_QUERY_PARAM } from '../helper/url-query-params';

/**
 * Selects the outlet named by ?outletId= in the page being opened, replacing whatever
 * outlet was selected before, so that a link such as
 * /dashboard/product-search?outletId=466&productIds=3005721
 * works in the store the link was built for.
 *
 * Used by AuthGuard, which checks the rights of the selected outlet and therefore has to
 * apply the outlet the link asks for before looking at them.
 */
@Injectable({
  providedIn: 'root',
})
export class OutletFromUrlService {
  //an outlet that cannot be used is worth reporting once, not on every retried navigation
  private readonly reportedOutletIds = new Set<string>();

  constructor(
    private outletCacheService: OutletCacheService,
    private toastrService: ToastrService,
    private router: Router
  ) {}

  /**
   * Completes once the outlet named in the url has been selected, so that whatever reads
   * the selected outlet next sees it. Completes right away when the url names no outlet,
   * and when the user has no access to the one it names, which is only reported.
   */
  applyOutletFromUrl(): Observable<void> {
    const requestedOutletId = this.getRequestedOutletId();

    if (requestedOutletId === null) return of(undefined);

    return this.outletCacheService.getSelectedOutlet().pipe(
      take(1),
      switchMap((selectedOutlet: OutletRightsDTO | null) =>
        selectedOutlet?.outlet?.identity?.id === Number(requestedOutletId)
          ? of(undefined)
          : this.selectOutlet(requestedOutletId)
      )
    );
  }

  private selectOutlet(requestedOutletId: string): Observable<void> {
    return this.outletCacheService.getOutlets().pipe(
      take(1),
      map((outlets: OutletRightsDTO[] | null) => {
        //the outlets of the logged in user are the ones they are allowed to work in, an id
        //that is not among them is one they have no access to
        const outlet = (outlets || []).find(
          (outletRights: OutletRightsDTO) =>
            outletRights.outlet.identity.id === Number(requestedOutletId)
        );

        if (!outlet) {
          this.reportOutletNotAvailable(requestedOutletId);
          return;
        }

        this.outletCacheService.setSelectedOutlet(outlet);
      })
    );
  }

  private reportOutletNotAvailable(requestedOutletId: string): void {
    if (this.reportedOutletIds.has(requestedOutletId)) return;

    this.reportedOutletIds.add(requestedOutletId);
    this.toastrService.warning(
      $localize`:@@toastr-url-outlet-not-available:Outlet ${requestedOutletId} is not available for your user.`
    );
  }

  /**
   * Reads the outlet id from the page being navigated to. Guards run while that navigation
   * is still in flight, and canLoad is not given the query params, so it is taken from the
   * current navigation rather than from an ActivatedRoute.
   */
  private getRequestedOutletId(): string | null {
    const navigation = this.router.getCurrentNavigation();
    const urlTree = navigation
      ? navigation.extractedUrl
      : this.router.parseUrl(this.router.url);
    const requestedOutletId = urlTree.queryParamMap.get(OUTLET_ID_QUERY_PARAM);

    return requestedOutletId?.trim() || null;
  }
}
